import * as Immutable from 'immutable';
import { createSelector } from 'reselect';

// Utils
import { prop } from '../../../common/utils/immutableHelper';
import { createDeepSelector, createShallowSelector } from '../../utils/milanoteReselect/milanoteReselect';
import { isElementOrAncestorInTrash } from '../../../common/elements/utils/elementTraversalUtils';
import { isAsyncEntityFetched } from '../../utils/services/http/asyncResource/asyncResourceUtils';
import { getTitle, getRootBoardProperty } from '../../../common/elements/utils/elementPropertyUtils';
import { getIsTemplate } from '../../../common/templates/templateUtil';

import {
    getTemplateId,
    getTemplateBoardId,
    matchTemplate,
    sortMatchedTemplates,
    TEMPLATE_TYPES,
} from './boardTemplatePickerUtil';

import { getUserEnableTemplatePicker } from '../../../common/users/utils/userPropertyUtils';

import { canSave } from '../../../common/permissions/permissionUtil';

// Selectors
import { getCmsData } from '../../cms/cmsSelector';
import { getAsyncResourceEntityState } from '../../utils/services/http/asyncResource/asyncResourceSelector';
import { getCurrentBoardResourceState } from '../../element/board/boardSelector';
import { boardVisibleElementGraphSelector } from '../../element/selectors/elementGraphSelector';
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { getElements } from '../../element/selectors/elementsSelector';
import {
    getBoardSummariesArraySelector,
    getBoardSummariesFetched,
} from '../../element/board/summary/boardSummariesSelector';
import { getIsModalOpen } from '../../components/modal/activeModalSelector';
import { getIsAnnotationModeEnabled } from '../../element/drawing/annotation/store/annotationSelector';

// Constants
import { CmsEntities } from '../../cms/cmsConstants';
import { ResourceTypes } from '../../utils/services/http/asyncResource/asyncResourceConstants';

const EMPTY_MAP = Immutable.Map();
const EMPTY_LIST = Immutable.List();

const EMPTY_ARRAY = [];

const getTemplatesCmsData = getCmsData(CmsEntities.TEMPLATES);

const getCmsTemplates = (state) => prop('templates', getTemplatesCmsData(state));
const getCmsDefaultTemplates = (state) => prop('defaultTemplates', getTemplatesCmsData(state)) || EMPTY_LIST;
const getCmsCommonBoardTitles = (state) => prop('commonBoardTitles', getTemplatesCmsData(state)) || EMPTY_LIST;

const getCmsTemplateCollections = (state) => prop('collections', getTemplatesCmsData(state));

export const getCmsTemplatesFetched = (state) =>
    isAsyncEntityFetched(getAsyncResourceEntityState(state, ResourceTypes.cms, CmsEntities.TEMPLATES));

const getOnboardDisableTemplatePicker = (state) =>
    state.getIn(['app', 'onboarding', 'metadata', 'disableTemplatePicker']);
export const getOnboardHideTemplatePicker = (state) =>
    state.getIn(['app', 'onboarding', 'metadata', 'hideTemplatePicker']);

export const getOnboardTemplateLandingPage = (state) => state.getIn(['app', 'onboarding', 'metadata', 'landing_page']);
export const getOnboardSelectTemplate = (state) => state.getIn(['app', 'onboarding', 'metadata', 'select_template']);

const customTemplatesSelector = createDeepSelector(
    getBoardSummariesArraySelector,
    getElements,
    getBoardSummariesFetched,
    (boardSummaries, elements) =>
        Immutable.Map(
            boardSummaries.reduce((acc, { isTemplate, id, title, parentId }) => {
                if (!isTemplate) return acc;

                if (isElementOrAncestorInTrash(elements, id)) return acc;

                acc[id] = Immutable.Map({
                    id,
                    title,
                    templateBoardId: id,
                    type: TEMPLATE_TYPES.CUSTOM,
                    keywords: '',
                    forceClear: true,
                });

                return acc;
            }, {}),
        ),
);

export const templatesSelector = createSelector(
    getCmsTemplates,
    customTemplatesSelector,
    (builtInTemplates, customTemplates) => {
        if (!builtInTemplates) return EMPTY_MAP;

        return builtInTemplates.merge(customTemplates);
    },
);

export const defaultTemplateListingSelector = createSelector(
    getCmsDefaultTemplates,
    getCmsCommonBoardTitles,
    templatesSelector,
    (_, { currentBoard }) => currentBoard,
    getOnboardTemplateLandingPage,
    (defaultTemplates, commonBoardTitles, templates, currentBoard, landingPage) => {
        const currentBoardTitle = getTitle(currentBoard) || '';
        const isCommonBoardTitle = commonBoardTitles.includes(currentBoardTitle.toLowerCase());
        // don't return suggestions based on board title if a landing page param is set or the if the board title is a common one
        if (landingPage || isCommonBoardTitle) {
            return defaultTemplates;
        }

        const searchResults =
            templates &&
            templates
                .filter(matchTemplate(currentBoardTitle))
                .toList()
                .sort(sortMatchedTemplates(currentBoardTitle, defaultTemplates))
                .take(1);

        return searchResults && searchResults.size
            ? searchResults
                  .concat(defaultTemplates)
                  // begin remove duplicate entries
                  .groupBy(getTemplateId)
                  .map((group) => group.first())
                  .toList()
                  // end remove duplicate entries
                  .take(5)
            : defaultTemplates;
    },
);

export const templateCollectionsSelector = createSelector(
    getCmsTemplateCollections,
    customTemplatesSelector,
    (templateCollections, customTemplates) => {
        const collections = templateCollections || EMPTY_LIST;

        if (customTemplates.size) {
            return collections.unshift(
                Immutable.Map({
                    id: '__custom-templates__',
                    title: 'Your custom templates',
                    templates: customTemplates.toList(),
                }),
            );
        }

        return collections;
    },
);

const getIsDragging = (state) => state.getIn(['app', 'dragging', 'isDragging']);

const currentBoardVisibleChildrenIdsSelector = createShallowSelector(
    boardVisibleElementGraphSelector,
    getCurrentBoardId,
    (visibleElementGraph, visibleBoardId) => visibleElementGraph[visibleBoardId] || EMPTY_ARRAY,
);

// (distinct from getIsCurrentVisibleBoardFetched, so that it doesn't hide the picker while loading a template board)
const getIsCurrentBoardFetched = (state, props) => isAsyncEntityFetched(getCurrentBoardResourceState(state, props));

export const templatePickerVisibleSelector = createSelector(
    (_, { currentUser }) => getUserEnableTemplatePicker(currentUser),
    getCmsTemplatesFetched,
    currentBoardVisibleChildrenIdsSelector,
    (_, { currentBoard }) => currentBoard,
    getIsCurrentBoardFetched,
    getIsDragging,
    getOnboardDisableTemplatePicker,
    getIsModalOpen,
    getIsAnnotationModeEnabled,
    (_, { permissions }) => permissions,
    (
        currentUserEnabledTemplatePicker,
        templatesFetched,
        currentBoardChildrenIds,
        currentBoard,
        isCurrentBoardFetched,
        isDragging,
        onboardingDisable,
        isModalOpen,
        isDrawingModeEnabled,
        permissions,
    ) =>
        currentUserEnabledTemplatePicker &&
        templatesFetched &&
        currentBoardChildrenIds.length === 0 &&
        !getIsTemplate(currentBoard) &&
        !getRootBoardProperty(currentBoard) &&
        isCurrentBoardFetched &&
        !isDragging &&
        !onboardingDisable &&
        !isModalOpen &&
        !isDrawingModeEnabled &&
        canSave(permissions),
);

export const selectedTemplateBoardSelector = createSelector(
    getElements,
    templatesSelector,
    (_, { selectedTemplateId }) => selectedTemplateId,
    (elements, templates, selectedTemplateId) =>
        elements.get(getTemplateBoardId(templates && templates.get(selectedTemplateId))),
);
